import { z } from "zod";
import { AgencyIdSchema, BusinessGraphIdSchema, ProjectIdSchema, RevisionIdSchema, } from "../primitives/ids.js";
import { BUSINESS_GRAPH_SCHEMA_VERSION, SchemaVersionSchema, } from "../primitives/schema-version.js";
import { BusinessGraphAssetV1Schema } from "./media-slot.js";
import { ComponentSelectionListSchema } from "./component-selection.js";
import { CompositionLockSchema } from "./lens.js";
import { MediaAssignmentSchema } from "./media-slot.js";
import { BusinessGraphProvenanceDomainV1Schema } from "./business-graph-provenance.js";
const EvidenceRecordSchema = z
    .object({
    id: z.string().min(1),
    evidenceType: z.string().min(1),
    assetId: z.string().optional(),
    referenceUri: z.string().optional(),
})
    .strict();
export const BusinessGraphLocationV1Schema = z
    .object({
    id: z.string().min(1),
    name: z.string().min(1),
    addressLine1: z.string().max(200).optional(),
    addressLine2: z.string().max(200).optional(),
    city: z.string().max(120).optional(),
    region: z.string().max(120).optional(),
    postalCode: z.string().max(40).optional(),
    country: z.string().max(80).optional(),
    latitude: z.number().min(-90).max(90).optional(),
    longitude: z.number().min(-180).max(180).optional(),
})
    .strict();
export const BusinessGraphAudienceV1Schema = z
    .object({
    primaryAudience: z.string().max(200).optional(),
    segments: z.array(z.object({ id: z.string().min(1), label: z.string().min(1) }).strict()).default([]),
    notes: z.string().max(2000).optional(),
})
    .strict();
/** Minimum Tier 0 BusinessGraph — required top-level fields and domains. */
export const BusinessGraphV1Schema = z
    .object({
    schemaVersion: SchemaVersionSchema,
    businessGraphId: BusinessGraphIdSchema,
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    revisionId: RevisionIdSchema,
    /** Required field; use "" before hash compute, then withComputedSnapshotHash (D-027). */
    snapshotHash: z.string(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    business: z
        .object({
        businessName: z.string().min(1),
        legalName: z.string().optional(),
        primaryPhone: z.string().optional(),
        primaryEmail: z.string().optional(),
        description: z.string().optional(),
    })
        .strict(),
    operations: z
        .object({
        serviceAreas: z
            .array(z.object({ id: z.string().optional(), name: z.string() }).strict())
            .default([]),
        locations: z.array(BusinessGraphLocationV1Schema).default([]),
        hours: z
            .array(z
            .object({
            dayOfWeek: z.number().int().min(0).max(6),
            open: z.string(),
            close: z.string().optional(),
        })
            .strict())
            .default([]),
        holidayHours: z
            .array(z.object({ date: z.string(), note: z.string().optional() }).strict())
            .default([]),
    })
        .strict(),
    services: z
        .array(z
        .object({
        id: z.string().min(1),
        name: z.string().min(1),
        restrictions: z
            .array(z.object({ id: z.string().min(1), text: z.string().min(1).max(500) }).strict())
            .default([]),
    })
        .strict())
        .default([]),
    audience: BusinessGraphAudienceV1Schema.default({ segments: [] }),
    trust: z
        .object({
        license: z.array(z.object({ id: z.string().min(1) }).strict()).default([]),
        insurance: z.array(z.object({ id: z.string().min(1) }).strict()).default([]),
        certifications: z.array(z.object({ id: z.string().min(1) }).strict()).default([]),
        claims: z.array(z.object({ id: z.string().min(1) }).strict()).default([]),
    })
        .strict(),
    content: z
        .object({
        faqs: z
            .array(z
            .object({
            id: z.string().min(1),
            question: z.string(),
            answer: z.string().optional(),
        })
            .strict())
            .default([]),
        offers: z
            .array(z.object({ id: z.string().min(1), title: z.string() }).strict())
            .default([]),
        announcements: z
            .array(z.object({ id: z.string().min(1), message: z.string() }).strict())
            .default([]),
    })
        .strict(),
    assets: z.array(BusinessGraphAssetV1Schema).default([]),
    layout: z
        .object({
        sectionOrder: z.array(z.string()).default([]),
        componentSelections: ComponentSelectionListSchema.default([]),
        mediaAssignments: z.array(MediaAssignmentSchema).default([]),
        lensId: z.string().min(1).optional(),
        strategyId: z.string().min(1).optional(),
        surfaceType: z.string().min(1).optional(),
        referenceId: z.string().min(1).optional(),
        compositionLock: CompositionLockSchema.optional(),
        /** Wave 2: selected site package stored in BusinessGraph.layout — no hidden package truth. */
        sitePackageId: z
            .enum([
            "package-one-page",
            "package-basic-3",
            "package-standard-5",
            "package-growth-8",
            "package-authority-10",
            "package-authority-15",
        ])
            .optional(),
        /** Wave 8: design selection reference — no hidden design truth. */
        designSelectionId: z.string().min(1).optional(),
        /** Tier 2: motion level stored on graph — QG enforced. */
        motionLevel: z.enum(["motion-0", "motion-1", "motion-2", "motion-3"]).optional(),
    })
        .strict(),
    branding: z
        .object({
        primaryColor: z.string().optional(),
        logoAssetId: z.string().optional(),
    })
        .strict(),
    permissions: z
        .object({
        clientSafeByDefault: z.boolean().default(true),
    })
        .strict(),
    evidence: z
        .object({
        records: z.array(EvidenceRecordSchema).default([]),
    })
        .strict(),
    /** BG-003: per-fact source/method/actor/time/confidence/review (parallel to evidence). */
    provenance: BusinessGraphProvenanceDomainV1Schema.default({ records: [] }),
})
    .strict();
/** Stable evidence record id for a client-declared primary evidence type from intake. */
export const INTAKE_PRIMARY_EVIDENCE_RECORD_ID = "evidence_intake_primary";
/**
 * Records a client-declared primary evidence type as a truthful evidence record.
 * `none_yet` or empty removes the declaration. This is a declaration (no asset/uri),
 * not fabricated proof — stricter truth-gate proof checks still apply.
 */
export function applyDeclaredPrimaryEvidence(graph, primaryEvidenceType) {
    const filtered = graph.evidence.records.filter((record) => record.id !== INTAKE_PRIMARY_EVIDENCE_RECORD_ID);
    if (primaryEvidenceType && primaryEvidenceType !== "none_yet") {
        filtered.push({
            id: INTAKE_PRIMARY_EVIDENCE_RECORD_ID,
            evidenceType: primaryEvidenceType,
        });
    }
    graph.evidence.records = filtered;
}
/** Placeholder before canonical hash is computed (excluded from hash input). */
export const UNCOMPUTED_SNAPSHOT_HASH = "";
export function createEmptyBusinessGraphV1(params) {
    const now = params.createdAt ?? new Date().toISOString();
    return BusinessGraphV1Schema.parse({
        schemaVersion: BUSINESS_GRAPH_SCHEMA_VERSION,
        businessGraphId: params.businessGraphId,
        agencyId: params.agencyId,
        projectId: params.projectId,
        revisionId: params.revisionId,
        snapshotHash: params.snapshotHash ?? UNCOMPUTED_SNAPSHOT_HASH,
        createdAt: now,
        updatedAt: params.updatedAt ?? now,
        business: { businessName: params.businessName },
        operations: { serviceAreas: [], locations: [], hours: [], holidayHours: [] },
        services: [],
        audience: { segments: [] },
        trust: { license: [], insurance: [], certifications: [], claims: [] },
        content: { faqs: [], offers: [], announcements: [] },
        assets: [],
        layout: {
            sectionOrder: ["hero", "services", "contact"],
            componentSelections: [],
            mediaAssignments: [],
        },
        branding: {},
        permissions: { clientSafeByDefault: true },
        evidence: { records: [] },
        provenance: { records: [] },
    });
}
//# sourceMappingURL=business-graph.js.map